import { LendingVaultExpanded } from "@bridgesplit/abf-react";
import { VaultMetadataTag } from "@bridgesplit/abf-sdk";
import { formatTokenAmount } from "@bridgesplit/utils";

import { DetailForm, initialDetailForm } from "../type";

export function getInitialDetailForm(vaultExpanded: LendingVaultExpanded | undefined): DetailForm {
    if (!vaultExpanded) return initialDetailForm;

    const metadata = vaultExpanded.vaultMetadata;
    return {
        name: metadata?.name ?? undefined,
        description: metadata?.description ?? undefined,
        managerName: metadata?.managerName ?? undefined,
        managerImage: metadata?.managerImage ?? undefined,
        bannerImage: metadata?.bannerImage ?? undefined,
        tokenImage: metadata?.tokenImage ?? undefined,
        themeColor: metadata?.themeColor ?? undefined,
        tags: metadata?.tags ?? undefined,
        depositCap: metadata?.depositCap != null ? metadata.depositCap : undefined
    };
}

export type DetailFormChangeStat = {
    caption: string;
    value: [string, string];
    hide: boolean;
};

export function getDetailFormChangeStats(
    form: DetailForm,
    initial: DetailForm,
    vaultExpanded: LendingVaultExpanded | undefined
): DetailFormChangeStat[] {
    if (!vaultExpanded) return [];

    const formatEmpty = (s: string | undefined) => (typeof s === "string" && s.length > 0 ? s : "—");
    const formatUrlAccess = (enabled: boolean) => (enabled ? "URL Access" : "Public");
    const initialHasUrlAccessOnly = hasUrlAccessOnlyTag(initial.tags);
    const formHasUrlAccessOnly = hasUrlAccessOnlyTag(form.tags);

    return [
        {
            caption: "Name",
            value: [formatEmpty(initial.name), formatEmpty(form.name)],
            hide: form.name === initial.name
        },
        {
            caption: "Manager name",
            value: [formatEmpty(initial.managerName), formatEmpty(form.managerName)],
            hide: form.managerName === initial.managerName
        },
        {
            caption: "Description",
            value: [formatEmpty(initial.description), formatEmpty(form.description)],
            hide: form.description === initial.description
        },
        {
            caption: `Cap ${vaultExpanded.principalMetadata.symbol}`,
            value: [
                formatTokenAmount(initial.depositCap, { symbol: vaultExpanded.principalMetadata.symbol }),
                formatTokenAmount(form.depositCap, { symbol: vaultExpanded.principalMetadata.symbol })
            ],
            hide:
                formatTokenAmount(form.depositCap, { symbol: vaultExpanded.principalMetadata.symbol }) ===
                formatTokenAmount(initial.depositCap, { symbol: vaultExpanded.principalMetadata.symbol })
        },
        {
            caption: "Brand color",
            value: [formatEmpty(initial.themeColor), formatEmpty(form.themeColor)],
            hide: form.themeColor === initial.themeColor
        },
        {
            caption: "Banner URL",
            value: [formatEmpty(initial.bannerImage), formatEmpty(form.bannerImage)],
            hide: form.bannerImage === initial.bannerImage
        },
        {
            caption: "Icon URL",
            value: [formatEmpty(initial.tokenImage), formatEmpty(form.tokenImage)],
            hide: form.tokenImage === initial.tokenImage
        },
        {
            caption: "Manager image URL",
            value: [formatEmpty(initial.managerImage), formatEmpty(form.managerImage)],
            hide: form.managerImage === initial.managerImage
        },
        {
            caption: "URL Access",
            value: [formatUrlAccess(initialHasUrlAccessOnly), formatUrlAccess(formHasUrlAccessOnly)],
            hide: initialHasUrlAccessOnly === formHasUrlAccessOnly
        }
    ];
}

export function hasUrlAccessOnlyTag(tags: VaultMetadataTag[] | undefined): boolean {
    return (tags ?? []).includes(VaultMetadataTag.UrlAccessOnly);
}

export function areTagsEqual(a: VaultMetadataTag[] | undefined, b: VaultMetadataTag[] | undefined): boolean {
    const arrA = a ?? [];
    const arrB = b ?? [];
    if (arrA.length !== arrB.length) return false;
    const setB = new Set(arrB);
    return arrA.every((t) => setB.has(t));
}

/** Required fields per LendingVaultMetadataApi (non-optional: name, description, managerName, managerImage) */
const REQUIRED_FIELDS: { key: keyof DetailForm; label: string }[] = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "managerName", label: "Manager name" },
    { key: "managerImage", label: "Manager image URL" }
];

export function getDetailFormValidationErrors(form: DetailForm): Partial<Record<keyof DetailForm, string>> {
    const errors: Partial<Record<keyof DetailForm, string>> = {};

    for (const { key, label } of REQUIRED_FIELDS) {
        const value = form[key];
        if (key === "tags") continue; // tags is array, handled separately
        const isEmpty = typeof value === "string" ? !value?.trim() : value === undefined;
        if (isEmpty) {
            errors[key] = `${label} is required`;
        }
    }

    // Add depositCap integer check only if present and not empty
    if (form.depositCap !== undefined) {
        if (!Number.isInteger(form.depositCap)) {
            errors.depositCap = "Deposit cap must be an integer";
        }
    }

    return errors;
}

export function hasDetailFormValidationErrors(form: DetailForm): boolean {
    return Object.keys(getDetailFormValidationErrors(form)).length > 0;
}

export function getDetailFormValidationErrorMessage(form: DetailForm): string | undefined {
    const errors = getDetailFormValidationErrors(form);
    const missingLabels = REQUIRED_FIELDS.filter(({ key }) => errors[key]).map(({ label }) => label);
    if (missingLabels.length === 0) return undefined;
    if (missingLabels.length === 1) return `${missingLabels[0]} is required`;
    return `Required: ${missingLabels.join(", ")}`;
}

export function isDetailFormSame(form: DetailForm, vaultExpanded: LendingVaultExpanded | undefined): boolean {
    const metadata = vaultExpanded?.vaultMetadata;
    if (!metadata) return true;

    const formTags = form.tags ?? [];
    const metadataTags = metadata.tags ?? [];
    const tagsSame = areTagsEqual(formTags, metadataTags);

    const depositCapSame = (() => {
        if (form.depositCap == null) return metadata.depositCap == null;
        if (metadata.depositCap == null) return false;
        return Math.abs(form.depositCap - metadata.depositCap) < 0.0001;
    })();

    return (
        (form.name ?? undefined) === (metadata.name ?? undefined) &&
        (form.description ?? undefined) === (metadata.description ?? undefined) &&
        (form.managerName ?? undefined) === (metadata.managerName ?? undefined) &&
        (form.managerImage ?? undefined) === (metadata.managerImage ?? undefined) &&
        (form.bannerImage ?? undefined) === (metadata.bannerImage ?? undefined) &&
        (form.tokenImage ?? undefined) === (metadata.tokenImage ?? undefined) &&
        (form.themeColor ?? undefined) === (metadata.themeColor ?? undefined) &&
        tagsSame &&
        depositCapSame
    );
}
